// =============================================================================
// exportEmployees — the clean employee list as an Excel file (8 Oct 2026)
//
// James, on the Employees screen: "Need a clean extra Excel report on employee
// list with details to copy / transfer into other client systems."
//
// What a client induction portal asks for, and nothing else: name, role,
// employment type, company, contact, start date, date of birth, address,
// next of kin — and a second sheet of current tickets with numbers and
// expiries. What NEVER goes in this file: TFN, bank, super, USI, medical
// notes, blood type, rates. Those live in employee_private for payroll and
// have no business in a client's system.
//
// Who sees the personal columns: the private table's own RLS decides —
// directors and payroll-access users get every row, anyone else gets only
// their own, so a manager's export has blank DOB/address for the rest.
// =============================================================================
import * as XLSX from "xlsx";
import { supabase } from "@/lib/supabase";

const d = (v: unknown) => (v ? String(v).slice(0, 10) : "");
const t = (s: string | null | undefined) => (s ?? "").replace(/_/g, " ");

export async function exportEmployeesXlsx(includeInactive: boolean): Promise<{ employees: number; licences: number }> {
  let q = supabase
    .from("v_employees")
    .select("employee_id, first_name, middle_name, last_name, preferred_name, email, mobile, home_phone, primary_role_name, employment_type, payroll_group, start_date, end_date, is_active, subcontractor_companies(legal_name, trading_name)")
    .order("first_name");
  if (!includeInactive) q = q.eq("is_active", true);
  const { data: emps, error } = await q;
  if (error) throw error;
  const rows = (emps ?? []) as any[];
  const ids = rows.map(r => r.employee_id);

  const [{ data: priv }, { data: lic }] = await Promise.all([
    supabase.from("employee_private")
      .select("employee_id, date_of_birth, address_line1, address_line2, suburb, state, postcode, next_of_kin_name, next_of_kin_relationship, next_of_kin_phone")
      .in("employee_id", ids),
    supabase.from("employee_licences")
      .select("employee_id, licence_number, issue_date, expiry_date, state_issued, verified_at, licence_types(display_name, category, issuing_body)")
      .in("employee_id", ids)
      .eq("is_active", true)
      .is("superseded_at", null)
      .order("expiry_date"),
  ]);
  const pv = new Map<string, any>((priv ?? []).map((p: any) => [p.employee_id, p]));
  const name = (r: any) => [r.first_name, r.last_name].filter(Boolean).join(" ");

  const empSheet = rows.map(r => {
    const p = pv.get(r.employee_id) ?? {};
    return {
      "Full name":        [r.first_name, r.middle_name, r.last_name].filter(Boolean).join(" "),
      "Preferred name":   r.preferred_name ?? "",
      "Role":             r.primary_role_name ?? "",
      "Employment type":  t(r.employment_type),
      "Company":          r.subcontractor_companies?.trading_name ?? r.subcontractor_companies?.legal_name ?? "Coretel Group Pty Ltd",
      "Email":            r.email ?? "",
      "Mobile":           r.mobile ?? "",
      "Home phone":       r.home_phone ?? "",
      "Start date":       d(r.start_date),
      "End date":         d(r.end_date),
      "Date of birth":    d(p.date_of_birth),
      "Address":          [p.address_line1, p.address_line2].filter(Boolean).join(", "),
      "Suburb":           p.suburb ?? "",
      "State":            p.state ?? "",
      "Postcode":         p.postcode ?? "",
      "Next of kin":      p.next_of_kin_name ?? "",
      "NOK relationship": p.next_of_kin_relationship ?? "",
      "NOK phone":        p.next_of_kin_phone ?? "",
      "Active":           r.is_active ? "Yes" : "No",
    };
  });

  const byId = new Map(rows.map(r => [r.employee_id, r]));
  const licSheet = ((lic ?? []) as any[]).map(l => ({
    "Employee":        name(byId.get(l.employee_id) ?? {}),
    "Licence / ticket": l.licence_types?.display_name ?? "",
    "Category":        l.licence_types?.category ?? "",
    "Issuing body":    l.licence_types?.issuing_body ?? "",
    "Number":          l.licence_number ?? "",
    "State issued":    l.state_issued ?? "",
    "Issue date":      d(l.issue_date),
    "Expiry date":     d(l.expiry_date),
    "Verified":        l.verified_at ? "Yes" : "No",
  })).sort((a, b) => a.Employee.localeCompare(b.Employee) || a["Licence / ticket"].localeCompare(b["Licence / ticket"]));

  const wb = XLSX.utils.book_new();
  const ws1 = XLSX.utils.json_to_sheet(empSheet);
  const ws2 = XLSX.utils.json_to_sheet(licSheet.length ? licSheet : [{ "Employee": "", "Licence / ticket": "" }]);
  const widths = (sheet: Record<string, unknown>[]) =>
    Object.keys(sheet[0] ?? {}).map(k => ({ wch: Math.min(40, Math.max(k.length, ...sheet.map(r => String(r[k] ?? "").length)) + 2) }));
  ws1["!cols"] = widths(empSheet); ws2["!cols"] = widths(licSheet);
  XLSX.utils.book_append_sheet(wb, ws1, "Employees");
  XLSX.utils.book_append_sheet(wb, ws2, "Licences");
  XLSX.writeFile(wb, `Coretel_Employees_${new Date().toISOString().slice(0, 10)}.xlsx`);
  return { employees: empSheet.length, licences: licSheet.length };
}
